import type { AdvisorItemKind } from '@travel-planner/shared/advisor-draft';
import type {
  CommissionCategory,
  ResolvedCommissionRates,
} from '@travel-planner/shared/pricing';
import type { HotelAvgRoomNight } from '@travel-planner/shared/schemas/products';
import type { PlannerDayService } from '@travel-planner/shared/utils/planner-day-services';
import {
  agencyCommissionFormulaAmount,
  DEFAULT_COMMISSION_RATES,
} from '@travel-planner/shared/pricing';
import { createContext, useContext } from 'react';

// XT-964 — the agency's commission per category in the Mini Planner. The host
// loads the rates for the DMC planning the trip; without a host the planner
// shows the product default for every category.

export const PlannerCommissionRatesContext =
  createContext<ResolvedCommissionRates>(DEFAULT_COMMISSION_RATES);

export const usePlannerCommissionRates = (): ResolvedCommissionRates =>
  useContext(PlannerCommissionRatesContext);

const CATEGORY_OF_KIND: Record<AdvisorItemKind, CommissionCategory> = {
  hotel: 'Accommodation',
  experience: 'Experiences',
  restaurant: 'Dining',
};

export const commissionCategoryOfKind = (
  kind: AdvisorItemKind
): CommissionCategory => CATEGORY_OF_KIND[kind];

/**
 * A day's Guide or Transport line: an experience's Guide and Transport count
 * as the experience; a half or full day's Transport is XT's own
 * Transportation service (T-405).
 */
export const commissionCategoryOfDayService = (
  service: Pick<PlannerDayService, 'kind' | 'span'>
): CommissionCategory =>
  service.kind === 'transport' && service.span !== null
    ? 'Transportation'
    : 'Experiences';

/** A price with its category's commission on top, as the quote computes it. */
export const withCommissionOnTop = (amount: number, percent: number): number =>
  amount + agencyCommissionFormulaAmount(amount, percent);

export type PricedItem = {
  kind: AdvisorItemKind;
  clientPrice?: number | null;
  childPrice?: number | null;
  avgRoomNight?: HotelAvgRoomNight | null;
};

/**
 * A product or itinerary item with its prices — adult, child and a hotel's
 * sold average — carrying its category's commission on top.
 */
export const itemWithCommissionOnTop = <T extends PricedItem>(
  item: T,
  rates: ResolvedCommissionRates
): T => {
  const percent = rates.byCategory[commissionCategoryOfKind(item.kind)];
  const onTop = (value: number | null | undefined) =>
    value == null ? value : withCommissionOnTop(value, percent);
  const average = item.avgRoomNight;
  return {
    ...item,
    clientPrice: onTop(item.clientPrice),
    childPrice: onTop(item.childPrice),
    ...(average
      ? {
          avgRoomNight: {
            ...average,
            avgSellPriceEur: withCommissionOnTop(
              average.avgSellPriceEur,
              rates.byCategory.Accommodation
            ),
          },
        }
      : {}),
  };
};

/** The estimate's amount per commission category. */
export type CommissionAmounts = Record<CommissionCategory, number>;

export const emptyCommissionAmounts = (): CommissionAmounts => ({
  Accommodation: 0,
  Experiences: 0,
  Dining: 0,
  Transportation: 0,
});

/**
 * What the agency earns: each category's line, as on the quote.
 *
 * The commission on an amount is computed exactly as the quote's Agency
 * Commission line computes it (TCP-PR07-037): ONE gross-up for every category
 * (XT-1014) — the rate is the agency's share of the final selling price, so it
 * is added on top of the price.
 */
export const estimateAgencyCommissionByCategory = (
  amounts: CommissionAmounts,
  rates: ResolvedCommissionRates
): number =>
  (Object.keys(amounts) as CommissionCategory[]).reduce(
    (sum, category) =>
      sum +
      agencyCommissionFormulaAmount(
        amounts[category],
        rates.byCategory[category]
      ),
    0
  );

/** What the agency earns, in whole units. */
export const estimateAgencyEarnByCategory = (
  amounts: CommissionAmounts,
  rates: ResolvedCommissionRates
): number => Math.round(estimateAgencyCommissionByCategory(amounts, rates));
